// jobSheetFromQuote.js — the internal Job Sheet for a SAVED quote, built from
// its Quote Log row (State JSON + cost breakdown), so it can be printed from
// the Quote Log without opening the calculator. Same layout as the
// calculator's Job Sheet (jobSheet.js); a few live-only figures (production /
// setup split, fuel litres) aren't stored with the quote, so this shows the
// saved totals instead.

import { buildJobSheetHTML } from "./jobSheet";
import { quoteDateStr } from "./generateQuoteDocument";
import { AUTO_SERVICE_HOURS, AUTO_PROTECTION, AUTO_DETAILING, autoAddonsSummary } from "./autoCatalogue";
import { SERVICE_CATEGORIES } from "./serviceCategories";
import { resolveClientScope, scopeTextToHTML } from "./scopeTemplates";

const num = (n, dp = 1) => (Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: dp });
const money = n => n == null ? "" : "£" + (Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// "2026-11-12" -> "12 November 2026"
const sheetDate = iso => iso ? quoteDateStr(String(iso).split("-").reverse().join("/")) : "";

export function buildJobSheetHTMLFromQuote(quote) {
  const st = quote["State JSON"] ? JSON.parse(quote["State JSON"]) : null;
  if (!st) throw new Error("This quote has no saved details to build a job sheet from.");
  const cb = st.costBreakdown || {};
  const notes = st.notes || {};
  const job = st.job || {};
  const inUnit = job.jobLocation === "in-unit";
  const refNo = quote["Quote Number"];
  const dateStr = quoteDateStr(quote["Date Created"]);
  const blocks = [
    { title: "Client scope", text: resolveClientScope(st), html: scopeTextToHTML(resolveClientScope(st)) },
    { title: "Estimator notes", text: notes.estimator },
    { title: "Internal notes", text: notes.internal },
    { title: "Exclusions", text: notes.exclusions },
  ];
  const energy = [inUnit ? "Electricity" : "Fuel", money(inUnit ? cb.electricityCost : cb.fuelCost)];

  if (quote["Category"] === "Automotive") {
    const v = st.vehicle || {}, c = st.client || {}, rates = st.rates || {};
    const vehicleName = [v.year, v.make, v.model].filter(Boolean).join(" ");
    return buildJobSheetHTML({
      heading: `Automotive job${[v.make, v.model].filter(Boolean).length ? `, ${vehicleName}` : ""}`,
      refNo, dateStr,
      sections: [
        { title: "Job", rows: [["Estimator", job.estimator], ["Work type", job.workType], ["Scheduled date", sheetDate(job.jobDate)], ["Job location", inUnit ? "In-unit (mains power)" : "On-site (generator)"]] },
        { title: "Client & vehicle", rows: [["Client", c.name], ["Phone", c.phone], ["Email", c.email], ["Address", [c.address, c.postcode].filter(Boolean).join(", ")], ["Vehicle", vehicleName], ["Registration", v.reg ? String(v.reg).toUpperCase() : ""], ["Colour", v.colour], ["Type", v.vehicleType], ["Contamination", st.contamination]] },
        { title: "Work", rows: [
          ["Services", (st.services || []).map(k => AUTO_SERVICE_HOURS[k]?.label).filter(Boolean).join(", ")],
          ["Protection", st.protection && st.protection !== "none" ? AUTO_PROTECTION[st.protection]?.label : ""],
          ["Add-ons", autoAddonsSummary(st.addons, st.services, st.protection)],
          ["Detailing", (st.detailing || []).map(k => AUTO_DETAILING[k]?.label).filter(Boolean).join(", ")],
          ["Concierge", st.concierge ? `${st.conciergeMiles || 0} miles${st.conciergeReturn ? ", return" : ", one way"}` : ""],
          ["Custom labour", (cb.customLabour || []).map(l => `${l.label.length > 60 ? l.label.slice(0, 59) + "…" : l.label} (${num(l.hours)}h)`).join("; ")],
        ] },
        { title: "Time & materials", rows: [["Labour (chargeable)", st.totalChargeableHrs != null ? `${num(st.totalChargeableHrs)} hrs` : ""], ["Operatives", rates.crewSize], ["Machine", st.machineType], ["Dry ice (inc. wastage)", cb.dryIceKgAdjusted != null ? `${num(cb.dryIceKgAdjusted)} kg` : ""], energy, ["Travel", (Number(rates.travelMiles) || 0) > 0 ? `${Math.round(rates.travelMiles)} miles` : ""]] },
      ],
      blocks,
    });
  }

  const service = st.service || {}, profile = st.profile || {}, deploy = st.deploy || {}, equip = st.equip || {};
  const catLabel = SERVICE_CATEGORIES.find(s => s.value === service.category)?.label || quote["Category"] || "Industrial";
  const vehicles = deploy.vehicles || [];
  const crew = vehicles.reduce((n, x) => n + (Number(x.crew) || 0), 0);
  return buildJobSheetHTML({
    heading: `${catLabel} job${service.subService ? `, ${service.subService}` : ""}`,
    refNo, dateStr,
    sections: [
      { title: "Job", rows: [["Estimator", job.estimator], ["Work type", job.workType], ["Scheduled date", sheetDate(job.jobDate)], ["Job location", inUnit ? "In-unit (mains power)" : "On-site (generator)"]] },
      { title: "Client & site", rows: [["Client", job.clientName], ["Contact", job.clientPOC], ["Phone", job.clientPhone], ["Email", job.clientEmail], ["Site", [job.siteName, job.postcode].filter(Boolean).join(", ")], ["Address", job.clientAddress]] },
      { title: "Time", rows: [["Total chargeable", st.totalChargeableHrs != null ? `${num(st.totalChargeableHrs)} hrs` : ""]] },
      { title: "Crew & kit", rows: [["Operatives", crew || ""], ["Vehicles", vehicles.length || ""], ["Compressors", vehicles.filter(x => x.compressor).length || ""], ["Machine", equip.machineType], ["Travel", (Number(deploy.travelMiles) || 0) > 0 ? `${Math.round(deploy.travelMiles)} miles` : ""], ["Overnight", deploy.overnight ? `${deploy.nights} night${deploy.nights > 1 ? "s" : ""}` : ""]] },
      { title: "Materials", rows: [["Dry ice (raw)", cb.dryIceKgRequired != null ? `${num(cb.dryIceKgRequired)} kg` : ""], ["Dry ice (inc. wastage)", cb.dryIceKgAdjusted != null ? `${num(cb.dryIceKgAdjusted)} kg` : ""], energy] },
      { title: "Surface", rows: [["Contamination", [profile.contaminationType, profile.severity].filter(Boolean).join(", ")], ["Surface", profile.surfaceType], ["Quantity", profile.quantity ? `${profile.quantity} ${profile.quantityUnit || ""}` : ""]] },
    ],
    blocks,
  });
}

// Writes html into a new window and opens the print dialog (Save as PDF).
// The window is opened by the caller synchronously on click so pop-up
// blockers allow it.
export function printHTML(win, html) {
  if (!win) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
  let printed = false;
  const go = () => { if (printed) return; printed = true; win.focus(); win.print(); };
  win.onload = go;
  setTimeout(go, 500);
}
